/**
 * Modelos de datos para cables, fibras y trazabilidad
 * @module types/fibers
 */

import { Coordinates } from './nodes'

// ============================================================================
// CABLE
// ============================================================================

export type TipoCable = 'troncal' | 'distribucion' | 'acometida' | 'drop'

export type EstadoCable = 'activo' | 'inactivo' | 'en_construccion'

export type TipoPuntoRuta = 'poste' | 'waypoint'

export interface PuntoRuta extends Coordinates {
  tipo: TipoPuntoRuta
  posteId?: string
}

export interface Cable {
  id: string
  nombre?: string  // Nombre legible: "FO-24 Centro-NAP1"
  codigo?: string  // Código corto: "CAB-001"
  tipo: TipoCable
  numeroFibras: number
  fabricante?: string
  modelo?: string

  // Geometría del cable
  ruta: PuntoRuta[]

  // Longitudes
  longitudTotal: number // metros (ruta geométrica, línea recta entre puntos)
  longitudUsada: number // metros

  // Gazas/reservas: rollo de cable de sobra cerca de cada punta (para poder
  // reabrir el empalme sin tender cable nuevo). Se suman a longitudTotal
  // para obtener la longitud real -- la lineal siempre va a parecer menor.
  reservaOrigenMetros?: number
  reservaDestinoMetros?: number

  // Estructura de fibras
  tieneBuffers: boolean
  buffers?: Buffer[]
  fibras?: Fibra[]

  // Conexiones
  origen: string // ID del nodo origen
  destino: string // ID del nodo destino

  // Estado
  estado: EstadoCable
  fechaInstalacion?: Date
  observaciones?: string

  // Metadata
  createdAt: Date
  updatedAt: Date
}

// ============================================================================
// BUFFER (Tubo de fibras)
// ============================================================================

export interface Buffer {
  numero: number
  color: ColorBuffer
  fibras: Fibra[]
}

// ============================================================================
// FIBRA
// ============================================================================

/**
 * Estados semánticos de una fibra dentro de un nodo (Cierre/NAP)
 * - libre: Disponible para uso
 * - en_paso: Pasa sin tocarse (pass-through) de un cable a otro
 * - fusionado: Empalmado directamente a otra fibra
 * - derivado: Cortado y conectado a entrada de splitter (sangrado)
 * - muerto: Lado del corte sin señal (hacia cliente cuando se sangra)
 * - reserva: Bucle/enrollado para uso futuro
 * - cliente: Conectado a ONT del cliente final
 * - olt: Puerto PON de OLT en POP (sincronizado desde config del nodo)
 */
export type EstadoFibra = 
  | 'libre' 
  | 'en_paso'
  | 'fusionado'
  | 'derivado'
  | 'muerto'
  | 'reserva'
  | 'cliente'
  | 'olt'

/**
 * Dirección de la fibra para determinar el lado "muerto" en sangrados
 */
export type DireccionFibra = 'hacia_olt' | 'hacia_cliente'

/**
 * Estado de UN extremo de una fibra. Un cable físico tiene dos puntas (origen
 * y destino del Cable), y cada punta se empalma/deriva/termina de forma
 * independiente de la otra -- la misma fibra puede estar fusionada en un
 * extremo y libre en el otro.
 */
export interface EstadoExtremoFibra {
  estado: EstadoFibra
  conectadoA: string | null
  splitterConectado?: {
    splitterId: string
    tipoPuerto: 'entrada' | 'salida'
    numeroPuerto?: number
  } | null
}

export interface Fibra {
  numero: number
  color: ColorFibra

  // ID único para trazabilidad
  // Formato: "CAB-01|azul|rojo" o "CAB-01|SB|azul"
  fibraId: string

  /** Estado real, por extremo. Fuente de verdad -- usar esto, no los campos de abajo. */
  extremos: {
    origen: EstadoExtremoFibra
    destino: EstadoExtremoFibra
  }

  // DEPRECADOS: agregado ("el más ocupado de los dos extremos") mantenido en
  // sincronía automáticamente solo para que los formularios que todavía no
  // migraron a leer `extremos` (NAPConfigForm, CierreConfigForm, CableConfigForm,
  // diagramas) no se rompan. No escribir estos campos a mano -- se derivan.
  estado: EstadoFibra
  conectadaA: string | null
  splitterConectado?: {
    splitterId: string
    tipoPuerto: 'entrada' | 'salida'
    numeroPuerto?: number
  }

  // Para sangrados: indica qué lado de la fibra es este
  direccion?: DireccionFibra

  // Mediciones
  atenuacion?: number // dB
  longitud?: number // metros

  notas?: string
}

// ============================================================================
// COLORES ESTÁNDAR
// ============================================================================

export type ColorFibra =
  | 'azul'
  | 'naranja'
  | 'verde'
  | 'marron'
  | 'gris'
  | 'blanco'
  | 'rojo'
  | 'negro'
  | 'amarillo'
  | 'violeta'
  | 'rosa'
  | 'turquesa'

export type ColorBuffer =
  | 'azul'
  | 'naranja'
  | 'verde'
  | 'marron'
  | 'gris'
  | 'blanco'
  | 'rojo'
  | 'negro'
  | 'amarillo'
  | 'violeta'
  | 'rosa'
  | 'turquesa'

export const FIBER_COLORS: readonly ColorFibra[] = [
  'azul',
  'naranja',
  'verde',
  'marron',
  'gris',
  'blanco',
  'rojo',
  'negro',
  'amarillo',
  'violeta',
  'rosa',
  'turquesa',
]

export const BUFFER_COLORS: readonly ColorBuffer[] = [
  'azul',
  'naranja',
  'verde',
  'marron',
  'gris',
  'blanco',
  'rojo',
  'negro',
  'amarillo',
  'violeta',
  'rosa',
  'turquesa',
]

// ============================================================================
// TRAZABILIDAD
// ============================================================================

/**
 * Resultado de trazar una fibra desde un punto
 */
export interface TraceResult {
  /** Indica si el trazado fue exitoso */
  success: boolean

  /** ID de la fibra trazada */
  fiberId: string

  /** ID de origen (cliente, puerto, etc.) */
  originId: string

  /** Ruta completa de fibras */
  path: PathSegment[]

  /** Distancia total en kilómetros */
  totalDistanceKm: number

  /** Atenuación total en dB */
  totalAttenuation: number

  /** Número de conectores en la ruta */
  connectorsCount: number

  /** Puntos de empalme (IDs de nodos) */
  splicingPoints: string[]

  /** Número de saltos (empalmes) */
  hops: number

  /** Timestamp del trazado */
  tracedAt: Date

  /** Mensaje de error si success es false */
  error?: string

  /**
   * Tramos donde la ruta topológica existe pero no hay empalme real
   * (FiberConnection o derivación de splitter) registrado en los datos —
   * la fibra podría no estar realmente pasando luz por ahí.
   */
  warnings: string[]
}

/**
 * Segmento de ruta en la trazabilidad
 */
export interface PathSegment {
  /** ID de la fibra */
  fiberId: string

  /** ID del cable */
  cableId: string

  /** Código legible del nodo (API), ej. N-000022 */
  nodeRefCode?: string

  /** Código legible del cable (API), ej. C-000007 */
  cableCodigo?: string

  /** Nombre del cable si existe (API) */
  cableNombre?: string

  /** Tipo de nodo */
  nodeType: 'cable' | 'cierre' | 'nap' | 'cliente' | 'pop'

  /** ID del nodo */
  nodeId: string

  /** Descripción legible */
  description: string

  /** Atenuación en este segmento (dB) */
  attenuation: number

  /** Distancia de este segmento (metros) */
  distance: number

  /** Distancia de este segmento (kilómetros) */
  distanceKm: number

  /** Indica si hay empalme en este segmento (basado en el tipo de nodo) */
  hasSplice: boolean

  /**
   * A diferencia de `hasSplice`, indica si el empalme/derivación fue
   * VERIFICADO contra datos reales (FiberConnection o splitter/bandeja
   * configurados en el nodo anterior). `undefined` cuando no aplica
   * verificación (primer tramo, o el nodo anterior no es CIERRE/NAP).
   * `false` significa que la ruta es solo topológica: no hay evidencia de
   * que la fibra realmente esté conectada ahí.
   */
  spliceVerificado?: boolean

  /** Coordenadas si aplica */
  coordinates?: Coordinates
}

/**
 * Conexión entre dos fibras (empalme)
 */
export interface FiberConnection {
  /** Fibra A */
  fiberA: string

  /** Fibra B */
  fiberB: string

  /** Tipo de conexión */
  tipo: 'empalme' | 'splitter' | 'conector'

  /** Atenuación de la conexión (dB) */
  atenuacion: number

  /** Nodo donde ocurre */
  nodeId: string

  /** Fecha de creación */
  createdAt: Date
}

/**
 * Grafo de conexiones global
 * Mapa: fibraId → fibraId conectada
 */
export type FiberConnectionMap = Map<string, string>

// ============================================================================
// UTILIDADES DE FIBRA ID
// ============================================================================

export interface ParsedFiberId {
  cableOrNodeId: string
  buffer: string | null // null si es "SB" (sin buffer)
  color: string
}

/**
 * Parsear un ID de fibra
 * @example
 * parseFiberId("CAB-001|azul|rojo")
 * // { cableOrNodeId: "CAB-001", buffer: "azul", color: "rojo" }
 */
export function parseFiberId(fiberId: string): ParsedFiberId {
  const parts = fiberId.split('|')

  if (parts.length !== 3) {
    throw new Error(`ID de fibra inválido: ${fiberId}`)
  }

  return {
    cableOrNodeId: parts[0],
    buffer: parts[1] === 'SB' ? null : parts[1],
    color: parts[2],
  }
}

/**
 * Generar ID de fibra
 * @example
 * generateFiberId("CAB-001", "azul", "rojo")
 * // "CAB-001|azul|rojo"
 */
export function generateFiberId(
  cableOrNodeId: string,
  buffer: string | null,
  color: string
): string {
  const bufferPart = buffer || 'SB'
  return `${cableOrNodeId}|${bufferPart}|${color}`
}

// ============================================================================
// VALIDACIONES
// ============================================================================

export function isValidFiberId(fiberId: string): boolean {
  try {
    parseFiberId(fiberId)
    return true
  } catch {
    return false
  }
}

export function isSplitterOutput(fiberId: string): boolean {
  return fiberId.includes('_OUT_')
}

export function isCableSource(fiberId: string): boolean {
  return fiberId.startsWith('CAB-')
}

export function isOLTSource(fiberId: string): boolean {
  return fiberId.startsWith('OLT-') || fiberId.startsWith('POP-')
}
